import React from "react";
import {
    Sparkles, MessageSquareMore, LayoutTemplate, Plus, Settings, Code,
    Shield, Globe, Database, Server, Cloud, HardDrive, Zap, Layers,
    GitBranch, Box, Lock, Route, Brain, Workflow
} from "lucide-react";
import { SettingsMockup } from "@/components/docs/SettingsMockup";
import { SRSMockup, ChatMockup, TemplateMockup, NodeMockup, ToolbarMockup, CodeGenMockup, ModelTemplateMockup } from "@/components/docs/FeatureMockups";
import { InteractiveSRS } from "@/components/docs/InteractiveSRS";
import { InteractiveChat } from "@/components/docs/InteractiveChat";
import { InteractiveSettings } from "@/components/docs/InteractiveSettings";
import { InteractiveToolbar } from "@/components/docs/InteractiveToolbar";
import { InteractiveEndpoint } from "@/components/docs/InteractiveEndpoint";
import { InteractiveService } from "@/components/docs/InteractiveService";
import { InteractiveFunction } from "@/components/docs/InteractiveFunction";
import { InteractiveMiddleware } from "@/components/docs/InteractiveMiddleware";
import { InteractiveBackendChat } from "@/components/docs/InteractiveBackendChat";

// --- Types ---
export type DocFeature = {
    title: string;
    description: string;
    image?: React.ReactNode;
    points: string[];
    interactiveComponent?: React.ReactNode;
    imageOnLeft?: boolean;
    fullWidthComponent?: boolean;
};

export type DocSectionData = {
    id: string;
    title: string;
    icon?: React.ElementType;
    description: string;
    features: DocFeature[];
};

// --- Data ---
export const DATABASE_SECTIONS: DocSectionData[] = [
    {
        id: "srs",
        title: "Generate Schema (SRS)",
        icon: Sparkles,
        description: "Transform your ideas into a database schema instantly using AI.",
        features: [
            {
                title: "AI-Powered Schema Generation",
                description: "The SRS (Software Requirements Specification) generator takes a natural language description of your application and automatically constructs the necessary database models, fields, and relationships.",
                points: [
                    "Eliminates initial database design friction",
                    "Supports complex relationship inference",
                    "Accepts natural language or file uploads"
                ],
                interactiveComponent: <InteractiveSRS />
            }
        ]
    },
    {
        id: "chat",
        title: "Chat with Schema",
        icon: MessageSquareMore,
        description: "Iterate on your design by talking to your database architect.",
        features: [
            {
                title: "Context-Aware AI Architect",
                description: "The Chat Agent understands your current schema. You can ask it to add fields, change relationships, or explain complex data structures.",
                points: [
                    "Real-time schema updates via chat",
                    "Understand complex commands like 'Add M2M relation'",
                    "Explains design decisions on request"
                ],
                interactiveComponent: <InteractiveChat />,
                imageOnLeft: true
            }
        ]
    },
    {
        id: "templates",
        title: "Choose Template",
        icon: LayoutTemplate,
        description: "Jumpstart your project with industry-standard architectures.",
        features: [
            {
                title: "Production-Ready Architectures",
                description: "Templates are pre-built schemas for common use cases like E-commerce, SaaS, or Social Networks. They come with best-practice relationships and fields pre-configured.",
                points: [
                    "Save hours of setup time",
                    "Based on industry best practices",
                    "Fully customizable after import"
                ],
                image: <TemplateMockup />
            }
        ]
    },
    {
        id: "add-nodes",
        title: "Add Nodes",
        icon: Plus,
        description: "Manually craft your schema with granular control.",
        features: [
            {
                title: "Manual Node Creation",
                description: "Start from scratch by adding a single, empty node. You have full control to define every field, type, and constraint manually.",
                points: [
                    "Add a blank node with no fields",
                    "Manually define all properties",
                    "Perfect for custom data structures"
                ],
                image: <NodeMockup />
            },
            {
                title: "Model Templates",
                description: "Quickly add individual pre-configured models to your schema. Choose from a library of common entities like User, Auth, Orders, Bills, Comments, and Likes.",
                points: [
                    "Library of common models (User, Orders, etc.)",
                    "Pre-configured fields and relationships",
                    "Mix and match to build your unique schema"
                ],
                image: <ModelTemplateMockup />,
                imageOnLeft: true
            }
        ]
    },
    {
        id: "toolbar",
        title: "Toolbar Actions",
        icon: Settings,
        description: "Essential workspace controls for management and preview.",
        features: [
            {
                title: "Toolbar Actions",
                description: "Essential tools for managing your workspace state.",
                points: [],
                interactiveComponent: <InteractiveToolbar />,
                fullWidthComponent: true
            }
        ]
    }
];

export const BACKEND_SECTIONS: DocSectionData[] = [
    {
        id: "endpoints",
        title: "API Endpoints",
        icon: Route,
        description: "Design RESTful API endpoints with complete control over routes, methods, and responses.",
        features: [
            {
                title: "Visual Endpoint Builder",
                description: "Create API endpoints by defining HTTP methods, paths, authentication requirements, and response structures. Connect endpoints to functions, middlewares, and models through a visual interface.",
                points: [
                    "Support for all HTTP methods (GET, POST, PUT, DELETE, PATCH)",
                    "Define request/response schemas with validation",
                    "Configure authentication, rate limiting, and CORS per endpoint",
                    "Visual dependency mapping to functions and middlewares"
                ],
                interactiveComponent: <InteractiveEndpoint />
            }
        ]
    },
    {
        id: "services",
        title: "Business Services",
        icon: Layers,
        description: "Organize business logic into reusable service classes.",
        features: [
            {
                title: "Service Architecture",
                description: "Services are classes that encapsulate business logic and orchestrate multiple functions. They can depend on other services, models, and external APIs to create a clean, maintainable architecture.",
                points: [
                    "Define service classes with data members and methods",
                    "Manage dependencies on other services and models",
                    "Support for singleton patterns and dependency injection",
                    "Organize functions into logical business domains"
                ],
                interactiveComponent: <InteractiveService />,
                imageOnLeft: true
            }
        ]
    },
    {
        id: "functions",
        title: "Functions",
        icon: Box,
        description: "Build reusable functions that can be attached to services, middlewares, or endpoints.",
        features: [
            {
                title: "Function Builder",
                description: "Functions are the building blocks of your backend logic. Define input parameters, return types, implementation steps, and dependencies. Functions can be attached to services, middlewares, or endpoints.",
                points: [
                    "Define typed input parameters with validation rules",
                    "Specify return types and error handling strategies",
                    "Document implementation steps for AI code generation",
                    "Declare dependencies on services, models, and packages"
                ],
                interactiveComponent: <InteractiveFunction />
            }
        ]
    },
    {
        id: "middlewares",
        title: "Middleware",
        icon: Shield,
        description: "Create middleware for authentication, validation, logging, and custom request processing.",
        features: [
            {
                title: "Middleware Pipeline",
                description: "Middlewares are functional pieces that process requests before they reach your endpoints. Create authentication, authorization, validation, rate limiting, and custom middleware with configurable success and failure responses.",
                points: [
                    "Built-in types: Auth, RBAC, Validation, Rate Limiting, CORS",
                    "Define success and error response codes",
                    "Configure middleware execution order",
                    "Attach to specific endpoints or apply globally"
                ],
                interactiveComponent: <InteractiveMiddleware />,
                imageOnLeft: true
            }
        ]
    },
    {
        id: "backend-chat",
        title: "Backend Chat Agent",
        icon: Brain,
        description: "AI-powered assistant for designing and refining your backend architecture.",
        features: [
            {
                title: "Intelligent Backend Design",
                description: "The Backend Chat Agent understands your entire backend architecture. Ask it to create endpoints, services, functions, or middlewares. It analyzes dependencies and suggests best practices for clean architecture.",
                points: [
                    "Context-aware of all entities (endpoints, services, functions, middlewares)",
                    "Automatically resolves dependencies between entities",
                    "Suggests architectural improvements and patterns",
                    "Generates complete entity definitions from natural language"
                ],
                interactiveComponent: <InteractiveBackendChat />
            }
        ]
    },
    {
        id: "settings",
        title: "Settings",
        icon: Settings,
        description: "Configure the runtime, security, and infrastructure of your backend.",
        features: [
            {
                title: "Workspace Settings",
                description: "Configure the runtime, security, and infrastructure of your backend.",
                points: [],
                interactiveComponent: <InteractiveSettings />,
                fullWidthComponent: true
            }
        ]
    },
    {
        id: "codegen",
        title: "Generate Code",
        icon: Code,
        description: "Export production-ready backend code in seconds.",
        features: [
            {
                title: "Full-Stack Export",
                description: "The Code Generator takes your visual schema and settings to produce a full NestJS/Express (Node.js) or FastAPI (Python) backend. It generates Controllers, Services, DTOs, and TypeORM/Mongoose/SQLAlchemy entities with complete implementations.",
                points: [
                    "NestJS / Express / FastAPI framework support",
                    "TypeORM / Mongoose / SQLAlchemy integration",
                    "Clean, maintainable architecture",
                    "Complete with authentication, middleware, and error handling"
                ],
                image: <CodeGenMockup />
            }
        ]
    }
];

export const ARCHITECTURE_SECTIONS: DocSectionData[] = [
    {
        id: "entity-relationships",
        title: "Entity Relationships",
        icon: GitBranch,
        description: "Understand how different entities connect and depend on each other.",
        features: [
            {
                title: "Dependency Graph",
                description: "The platform maintains a complete dependency graph of all entities. Endpoints depend on Functions and Middlewares, Services orchestrate Functions, and Functions depend on Models and other Services.",
                points: [
                    "Visual representation of entity connections",
                    "Automatic dependency resolution",
                    "Prevents circular dependencies",
                    "Validates entity relationships before code generation"
                ],
                image: <div className="p-8 bg-background/50 rounded-xl border border-border">
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                            <span className="text-sm text-secondary-text">Endpoint → Function → Service → Model</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                            <span className="text-sm text-secondary-text">Endpoint → Middleware → Function</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span className="text-sm text-secondary-text">Service → Service (Orchestration)</span>
                        </div>
                    </div>
                </div>
            }
        ]
    },
    {
        id: "code-generation",
        title: "Code Generation Pipeline",
        icon: Workflow,
        description: "From visual design to production-ready code in one click.",
        features: [
            {
                title: "Multi-Runtime Support",
                description: "Generate code for Node.js (NestJS/Express) or Python (FastAPI) runtimes. The generator creates complete project structures with proper folder organization, dependency management, and configuration files.",
                points: [
                    "Node.js: NestJS or Express with TypeScript",
                    "Python: FastAPI with Pydantic and SQLAlchemy",
                    "Automatic package.json / requirements.txt generation",
                    "Docker and deployment configurations included"
                ],
                image: <div className="p-8 bg-background/50 rounded-xl border border-border space-y-3">
                    <div className="font-mono text-xs text-primary-text">
                        <div className="text-green-500">✓ Models & Schemas</div>
                        <div className="text-green-500">✓ Services & Business Logic</div>
                        <div className="text-green-500">✓ Controllers & Routes</div>
                        <div className="text-green-500">✓ Middleware & Guards</div>
                        <div className="text-green-500">✓ DTOs & Validation</div>
                        <div className="text-green-500">✓ Authentication & Authorization</div>
                        <div className="text-green-500">✓ Database Configuration</div>
                        <div className="text-green-500">✓ Docker & Deployment</div>
                    </div>
                </div>,
                imageOnLeft: true
            }
        ]
    }
];

export const ABOUT_SECTIONS: DocSectionData[] = [
    {
        id: "about-platform",
        title: "About the Platform",
        icon: Sparkles,
        description: "A revolutionary visual platform for building production-ready backend systems.",
        features: [
            {
                title: "Our Mission",
                description: "We believe that building backend systems should be accessible to everyone, not just expert developers. Our mission is to democratize backend development by providing a visual, intuitive platform that combines the power of AI with the precision of professional architecture.",
                points: [
                    "Make backend development accessible to everyone",
                    "Combine AI assistance with visual design tools",
                    "Enable rapid prototyping and production deployment",
                    "Maintain professional architecture standards"
                ],
                image: <div className="p-8 bg-gradient-to-br from-primary/20 to-purple-500/10 rounded-xl border border-border space-y-4">
                    <div className="flex items-center gap-3">
                        <Database className="w-8 h-8 text-primary" />
                        <span className="font-semibold text-primary-text">Visual Database Design</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Brain className="w-8 h-8 text-purple-500" />
                        <span className="font-semibold text-primary-text">AI-Powered Architecture</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Code className="w-8 h-8 text-green-500" />
                        <span className="font-semibold text-primary-text">Production-Ready Code</span>
                    </div>
                </div>
            }
        ]
    },
    {
        id: "key-features",
        title: "What Makes Us Different",
        icon: Zap,
        description: "Not just another code generator - a complete platform that understands your architecture.",
        features: [
            {
                title: "AI-Powered Design Assistance",
                description: "Two specialized AI agents work together to help you build optimal architectures. The Database Chat Agent ensures your schema follows Third Normal Form (3NF) and eliminates redundancy. The Backend Chat Agent understands entity relationships and guides you to clean, maintainable code.",
                points: [
                    "Database Chat Agent for schema normalization",
                    "Backend Chat Agent for architecture patterns",
                    "Natural language understanding for complex requests",
                    "Real-time validation and suggestions"
                ],
                imageOnLeft: true,
                image: <div className="p-8 bg-background/50 rounded-xl border border-border space-y-3">
                    <div className="flex items-start gap-3">
                        <Brain className="w-6 h-6 text-purple-500 shrink-0 mt-1" />
                        <div>
                            <div className="font-semibold text-primary-text mb-1">Intelligent Analysis</div>
                            <div className="text-sm text-secondary-text">Analyzes your architecture and suggests improvements</div>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <GitBranch className="w-6 h-6 text-amber-500 shrink-0 mt-1" />
                        <div>
                            <div className="font-semibold text-primary-text mb-1">Dependency Resolution</div>
                            <div className="text-sm text-secondary-text">Automatically manages entity relationships</div>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <Shield className="w-6 h-6 text-green-500 shrink-0 mt-1" />
                        <div>
                            <div className="font-semibold text-primary-text mb-1">Best Practices</div>
                            <div className="text-sm text-secondary-text">Enforces industry standards and patterns</div>
                        </div>
                    </div>
                </div>
            },
            {
                title: "Entity-Based Architecture",
                description: "Build with a clean separation of concerns using Endpoints, Services, Functions, and Middlewares. This architecture scales from simple APIs to complex microservices, maintaining clarity and maintainability throughout.",
                points: [
                    "Endpoints: RESTful API route definitions",
                    "Services: Business logic orchestration",
                    "Functions: Reusable logic blocks with typed parameters",
                    "Middlewares: Request processing pipeline"
                ],
                image: <div className="p-8 bg-background/50 rounded-xl border border-border">
                    <div className="font-mono text-xs space-y-2">
                        <div className="text-blue-500">→ Endpoint (API Route)</div>
                        <div className="ml-4 text-purple-500">→ Middleware (Auth, Validation)</div>
                        <div className="ml-8 text-green-500">→ Function (Handler)</div>
                        <div className="ml-12 text-amber-500">→ Service (Business Logic)</div>
                        <div className="ml-16 text-pink-500">→ Model (Data Access)</div>
                    </div>
                </div>
            }
        ]
    },
    {
        id: "capabilities",
        title: "Core Capabilities",
        icon: Box,
        description: "Everything you need to build, deploy, and scale your backend.",
        features: [
            {
                title: "Complete Backend Stack",
                description: "From database design to API deployment, we cover every aspect of backend development. Generate production-ready code with authentication, authorization, file storage, rate limiting, and more - all configured through visual tools.",
                points: [
                    "Visual database modeling with AI assistance",
                    "RESTful API endpoint designer",
                    "Service-oriented architecture builder",
                    "Built-in authentication (Email/Password, OAuth)",
                    "File storage integration (AWS S3, GCP, Azure)",
                    "Rate limiting and CORS configuration",
                    "Auto-scaling for cloud deployment",
                    "Complete Docker and deployment setup"
                ],
                fullWidthComponent: true,
                interactiveComponent: <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 p-6">
                    <div className="bg-surface/40 border border-border rounded-xl p-4">
                        <Database className="w-8 h-8 text-primary mb-3" />
                        <h4 className="font-bold text-primary-text mb-2">Database</h4>
                        <ul className="text-xs text-secondary-text space-y-1">
                            <li>• PostgreSQL</li>
                            <li>• MySQL</li>
                            <li>• MongoDB</li>
                            <li>• SQLite</li>
                        </ul>
                    </div>
                    <div className="bg-surface/40 border border-border rounded-xl p-4">
                        <Code className="w-8 h-8 text-green-500 mb-3" />
                        <h4 className="font-bold text-primary-text mb-2">Runtimes</h4>
                        <ul className="text-xs text-secondary-text space-y-1">
                            <li>• Node.js (NestJS)</li>
                            <li>• Node.js (Express)</li>
                            <li>• Python (FastAPI)</li>
                            <li>• TypeScript Support</li>
                        </ul>
                    </div>
                    <div className="bg-surface/40 border border-border rounded-xl p-4">
                        <Shield className="w-8 h-8 text-amber-500 mb-3" />
                        <h4 className="font-bold text-primary-text mb-2">Security</h4>
                        <ul className="text-xs text-secondary-text space-y-1">
                            <li>• JWT Authentication</li>
                            <li>• OAuth (Google)</li>
                            <li>• RBAC</li>
                            <li>• Rate Limiting</li>
                        </ul>
                    </div>
                    <div className="bg-surface/40 border border-border rounded-xl p-4">
                        <Cloud className="w-8 h-8 text-purple-500 mb-3" />
                        <h4 className="font-bold text-primary-text mb-2">Infrastructure</h4>
                        <ul className="text-xs text-secondary-text space-y-1">
                            <li>• Docker Ready</li>
                            <li>• Auto-scaling</li>
                            <li>• File Storage</li>
                            <li>• CORS Config</li>
                        </ul>
                    </div>
                </div>
            }
        ]
    },
    {
        id: "use-cases",
        title: "Perfect For",
        icon: Globe,
        description: "Whether you're a solo developer, startup, or enterprise - we've got you covered.",
        features: [
            {
                title: "Built for Every Team Size",
                description: "Our platform adapts to your needs, whether you're validating an MVP, building a production app, or standardizing architecture across an enterprise.",
                points: [
                    "Startups: Validate ideas quickly with production-ready backends",
                    "Solo Developers: Build complex systems with AI assistance",
                    "Enterprise Teams: Standardize architecture and enable collaboration",
                    "Agencies: Deliver client projects faster with consistent quality"
                ],
                image: <div className="p-8 bg-background/50 rounded-xl border border-border space-y-6">
                    <div className="flex items-start gap-4">
                        <div className="text-3xl">🚀</div>
                        <div>
                            <div className="font-bold text-primary-text mb-1">Startups & MVPs</div>
                            <div className="text-sm text-secondary-text">Go from idea to production in hours, not weeks</div>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="text-3xl">👨‍💻</div>
                        <div>
                            <div className="font-bold text-primary-text mb-1">Solo Developers</div>
                            <div className="text-sm text-secondary-text">AI assistance for architecture decisions</div>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="text-3xl">🏢</div>
                        <div>
                            <div className="font-bold text-primary-text mb-1">Enterprise Teams</div>
                            <div className="text-sm text-secondary-text">Standardize and collaborate effectively</div>
                        </div>
                    </div>
                </div>,
                imageOnLeft: true
            }
        ]
    }
];
